import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from '@emotion/styled';
import OTType from '@opentok/client';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, IconButton, Stack } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { demoModeAtom, ItemDefinitionsAtom } from '@/store';
import { useObjectDetection } from '@/hooks/useObjectDetection';
import { useDetectionAutoSurvey } from '@/hooks/useDetectionAutoSurvey';
import { ObjectDetectionOverlay } from './ObjectDetectionOverlay';
import { buildDetectionLabelMap } from '@/utils/buildDetectionLabelMap';

const loadableItemDefs = loadable(ItemDefinitionsAtom);

interface SurveyVideoCredentials {
  apiKey: string;
  sessionId: string;
  token: string;
}

const VideosContainer = styled.div`
  position: relative;
  height: 100%;

  & [data-video] {
    transition: all 0.3s;
  }

  & [data-is-primary='true'] {
    width: 100%;
    height: 100%;
  }

  & [data-is-primary='false'] {
    position: absolute;
  }

  &[data-has-subscriber='true'] {
    & [data-is-primary='true'] {
      z-index: 0;
    }

    & [data-is-primary='false'] {
      width: 25%;
      height: 25%;
      top: 10px;
      right: 10px;
      z-index: 1;
    }

    &[data-show-pip='false'] {
      & [data-is-primary='false'] {
        opacity: 0;
        z-index: 0;
      }
    }
  }
`;

const VideoSubscriber = styled.div``;
const VideoPublisher = styled.div``;

const VideoControlButton = styled(IconButton)`
  display: flex;
  border-radius: 50%;
  background: white;

  &:hover {
    background-color: white;
    transform: scale(1.1);
  }
`;

// const SESSION_STATES = {
//   DISCONNECTED: 'disconnected',
//   CONNECTING: 'connecting',
//   CONNECTED: 'connected',
// };

// const CONNECTING_STATES = [SESSION_STATES.CONNECTING, SESSION_STATES.CONNECTED];

const useSurveyVideo = ({
  apiKey,
  sessionId,
  token,
}: SurveyVideoCredentials) => {
  const initiated = useRef(false);
  const publisherRef = useRef<HTMLDivElement | null>(null);
  const subscriberRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [_session, setSession] = useState<OTType.Session | null>(null);
  const [publisher, setPublisher] = useState<OTType.Publisher | null>(null);
  const [subscriber, setSubscriber] = useState<OTType.Subscriber | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const hasSubscriber = Boolean(subscriber);

  const handleError = (error: OTType.OTError | undefined) => {
    if (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    let session: OTType.Session | undefined = undefined;

    const init = async () => {
      initiated.current = true;
      const OT = (await import('@opentok/client')).default;
      session = OT.initSession(apiKey, sessionId);

      OT.getDevices((_error, devices) => {
        const videoInputs = (devices || []).filter(
          ({ kind }) => kind === 'videoInput'
        );
        setHasMultipleCameras(videoInputs.length > 1);
      });

      const publisher = OT.initPublisher(
        publisherRef.current!,
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          showControls: false,
          fitMode: 'contain',
        },
        handleError
      );

      session.on('streamDestroyed', (_event) => {
        setSubscriber(null);
      });

      session.on('streamCreated', (event) => {
        const sub = session!.subscribe(
          event.stream,
          subscriberRef.current!,
          {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            showControls: false,
            fitMode: 'contain',
          },
          handleError
        );
        sub.on({
          disconnected: () => {
            setSubscriber(null);
          },
          connected: () => {
            setSubscriber(sub);
          },
          destroyed: () => {
            setSubscriber(null);
          },
        });
      });

      session.on('sessionDisconnected', (event) => {
        console.info('You were disconnected from the session.', event.reason);
      });

      session.connect(token, function (error) {
        if (error) {
          handleError(error);
        } else {
          session!.publish(publisher, handleError);
          setPublisher(publisher);
        }
      });
    };
    if (!(session || initiated.current)) {
      init();
    }

    return () => {
      // console.log('-- Unmounting --');
      // console.log('   session:', session);
      // console.log('   publisher:', publisher);
      // if (session?.isConnected) {
      //   console.log(' DISCONNECT');
      //   session?.disconnect();
      // }
    };
  }, []);

  return {
    error,
    publisher,
    subscriber,
    publisherRef,
    subscriberRef,
    hasSubscriber,
    hasMultipleCameras,
  };
};

const videoType = {
  publisher: 'publisher',
  subscriber: 'subscriber',
};
type VideoType = (typeof videoType)[keyof typeof videoType];

function useSurveyVideoControls(
  publisher: OTType.Publisher | null,
  subscriber: OTType.Subscriber | null,
  initialPrimary?: VideoType
) {
  const [showPip, setShowPip] = useState(true);
  const [publishAudio, setPublishAudio] = useState(true);
  const [publishVideo, setPublishVideo] = useState(true);
  const [primaryVideo, setPrimaryVideo] = useState<VideoType>(
    initialPrimary || videoType.publisher
  );

  const togglePip = () => {
    setShowPip((prev) => !prev);
  };

  const toggleAudio = () => {
    publisher?.publishAudio(!publishAudio);
    setPublishAudio((prev) => !prev);
  };

  const toggleVideo = () => {
    publisher?.publishVideo(!publishVideo);
    setPublishVideo((prev) => !prev);
  };

  const toggleVideoSource = () => {
    publisher?.cycleVideo();
  };

  const downloadImage = (
    contentType: string,
    base64Data: string,
    fileName: string
  ) => {
    const a = document.createElement('a');
    a.href = `data:${contentType};base64,${base64Data}`;
    a.download = fileName;
    a.click();
  };

  const takeScreenshot = () => {
    const base64Data = subscriber?.getImgData() || publisher?.getImgData();
    if (base64Data) {
      downloadImage('image/png', base64Data, `Image-${uuidv4()}`);
    }
  };

  return {
    togglePip,
    toggleAudio,
    toggleVideo,
    toggleVideoSource,
    setPrimaryVideo,
    showPip,
    publishAudio,
    publishVideo,
    primaryVideo,
    takeScreenshot,
  };
}

interface SurveyVideoProps extends SurveyVideoCredentials {
  demoMode?: boolean;
}

interface SurveyVideoState {
  error: string | null;
  publisher: OTType.Publisher | null;
  subscriber: OTType.Subscriber | null;
  publisherRef: React.RefObject<HTMLDivElement | null>;
  subscriberRef: React.RefObject<HTMLDivElement | null>;
  hasSubscriber: boolean;
  hasMultipleCameras: boolean;
}

const useMockSurveyVideo = (): SurveyVideoState => {
  const initiated = useRef(false);
  const publisherRef = useRef<HTMLDivElement | null>(null);
  const subscriberRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publisher, setPublisher] = useState<OTType.Publisher | null>(null);
  const [subscriber, setSubscriber] = useState<OTType.Subscriber | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const hasSubscriber = Boolean(subscriber);

  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);

        const pubVideo = document.createElement('video');
        pubVideo.srcObject = stream;
        pubVideo.autoplay = true;
        pubVideo.muted = true;
        pubVideo.playsInline = true;
        pubVideo.style.cssText = 'width:100%;height:100%;object-fit:contain;';
        publisherRef.current?.appendChild(pubVideo);

        let currentDeviceIndex = 0;

        const publisherShim = {
          publishAudio: (enabled: boolean) => {
            stream.getAudioTracks().forEach((t) => {
              t.enabled = enabled;
            });
          },
          publishVideo: (enabled: boolean) => {
            stream.getVideoTracks().forEach((t) => {
              t.enabled = enabled;
            });
          },
          cycleVideo: async () => {
            const updated = await navigator.mediaDevices.enumerateDevices();
            const inputs = updated.filter((d) => d.kind === 'videoinput');
            if (inputs.length <= 1) return;
            currentDeviceIndex = (currentDeviceIndex + 1) % inputs.length;
            const nextStream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: inputs[currentDeviceIndex].deviceId },
              },
              audio: false,
            });
            const newTrack = nextStream.getVideoTracks()[0];
            const oldTrack = stream.getVideoTracks()[0];
            stream.removeTrack(oldTrack);
            stream.addTrack(newTrack);
            oldTrack.stop();
            pubVideo.srcObject = stream;
          },
          getImgData: () => {
            const canvas = document.createElement('canvas');
            canvas.width = pubVideo.videoWidth;
            canvas.height = pubVideo.videoHeight;
            canvas.getContext('2d')?.drawImage(pubVideo, 0, 0);
            return canvas.toDataURL('image/png').split(',')[1];
          },
        };

        setPublisher(publisherShim as unknown as OTType.Publisher);

        const DEMO_START_TIME = 4;

        const subVideo = document.createElement('video');
        subVideo.src = '/mock-remote.mp4';
        subVideo.autoplay = true;
        subVideo.muted = true;
        subVideo.playsInline = true;
        subVideo.style.cssText = 'width:100%;height:100%;object-fit:contain;';

        subVideo.addEventListener('loadedmetadata', () => {
          subVideo.currentTime = DEMO_START_TIME;
        });
        subVideo.addEventListener('ended', () => {
          subVideo.currentTime = DEMO_START_TIME;
          subVideo.play();
        });

        subscriberRef.current?.appendChild(subVideo);

        const subscriberShim = {
          getImgData: () => {
            const canvas = document.createElement('canvas');
            canvas.width = subVideo.videoWidth;
            canvas.height = subVideo.videoHeight;
            canvas.getContext('2d')?.drawImage(subVideo, 0, 0);
            return canvas.toDataURL('image/png').split(',')[1];
          },
        };

        setSubscriber(subscriberShim as unknown as OTType.Subscriber);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Mock video init failed');
      }
    };

    init();
  }, []);

  return {
    error,
    publisher,
    subscriber,
    publisherRef,
    subscriberRef,
    hasSubscriber,
    hasMultipleCameras,
  };
};

function SurveyVideoInner({
  publisher,
  subscriber,
  publisherRef,
  subscriberRef,
  hasSubscriber,
  hasMultipleCameras,
}: SurveyVideoState) {
  const {
    togglePip,
    toggleAudio,
    toggleVideo,
    toggleVideoSource,
    setPrimaryVideo,
    showPip,
    publishAudio,
    publishVideo,
    primaryVideo,
    takeScreenshot,
  } = useSurveyVideoControls(
    publisher,
    subscriber,
    hasSubscriber ? videoType.subscriber : videoType.publisher
  );

  const isDemoMode = useAtomValue(demoModeAtom);
  const [detectionEnabled, setDetectionEnabled] = useState(false);

  const itemDefsLoadable = useAtomValue(loadableItemDefs);
  const itemDefinitions =
    itemDefsLoadable.state === 'hasData' ? itemDefsLoadable.data : [];
  const detectionLabelMap = useMemo(
    () => buildDetectionLabelMap(itemDefinitions),
    [itemDefinitions]
  );

  // Always detect on the subscriber (home view) when present, otherwise publisher
  const detectionRef = hasSubscriber ? subscriberRef : publisherRef;
  const { predictionsRef, loading: detectionLoading } = useObjectDetection(
    detectionRef,
    isDemoMode && detectionEnabled
  );

  useDetectionAutoSurvey(detectionRef, isDemoMode && detectionEnabled);

  useEffect(() => {
    if (hasSubscriber) {
      setPrimaryVideo(videoType.subscriber);
    } else {
      setPrimaryVideo(videoType.publisher);
    }
  }, [hasSubscriber]);

  return (
    <VideosContainer
      data-has-subscriber={hasSubscriber}
      data-show-pip={showPip}
    >
      <VideoPublisher
        data-video={videoType.publisher}
        data-is-primary={primaryVideo === videoType.publisher}
        onClick={() => setPrimaryVideo(videoType.publisher)}
        ref={publisherRef as React.RefObject<HTMLDivElement>}
      />
      <VideoSubscriber
        data-video={videoType.subscriber}
        data-is-primary={primaryVideo === videoType.subscriber}
        onClick={() => setPrimaryVideo(videoType.subscriber)}
        ref={subscriberRef as React.RefObject<HTMLDivElement>}
      />

      {detectionEnabled && (
        <ObjectDetectionOverlay
          predictionsRef={predictionsRef}
          containerRef={detectionRef}
          labelMap={detectionLabelMap}
        />
      )}

      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          bottom: 0,
          width: 1,
          pb: 1,
          px: 1,
          gap: 2,
          justifyContent: 'center',
          zIndex: 3,
        }}
      >
        {hasSubscriber ? (
          <VideoControlButton onClick={togglePip}>
            <PictureInPictureIcon sx={{ opacity: showPip ? 1 : 0.5 }} />
          </VideoControlButton>
        ) : null}

        {hasMultipleCameras ? (
          <VideoControlButton onClick={toggleVideoSource}>
            <FlipCameraIosIcon />
          </VideoControlButton>
        ) : null}

        <VideoControlButton onClick={takeScreenshot}>
          <CameraAltIcon />
        </VideoControlButton>

        {isDemoMode && (
          <VideoControlButton
            onClick={() => setDetectionEnabled((prev) => !prev)}
          >
            {detectionLoading ? (
              <CircularProgress size={24} />
            ) : (
              <SearchIcon sx={{ opacity: detectionEnabled ? 1 : 0.5 }} />
            )}
          </VideoControlButton>
        )}

        <VideoControlButton onClick={toggleAudio}>
          {publishAudio ? <MicIcon /> : <MicOffIcon />}
        </VideoControlButton>

        <VideoControlButton onClick={toggleVideo}>
          {publishVideo ? <VideocamIcon /> : <VideocamOffIcon />}
        </VideoControlButton>
      </Stack>
    </VideosContainer>
  );
}

function SurveyVideoReal({ apiKey, sessionId, token }: SurveyVideoCredentials) {
  const state = useSurveyVideo({ apiKey, sessionId, token });
  return <SurveyVideoInner {...state} />;
}

function SurveyVideoMock() {
  const state = useMockSurveyVideo();
  return <SurveyVideoInner {...state} />;
}

export function SurveyVideo({
  apiKey,
  sessionId,
  token,
  demoMode,
}: SurveyVideoProps) {
  if (demoMode) {
    return <SurveyVideoMock />;
  }
  return (
    <SurveyVideoReal apiKey={apiKey} sessionId={sessionId} token={token} />
  );
}
